import { useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import DocumentTemplateEditor from '@/components/tiptap-editor/DocumentTemplateEditor';
import OnlyOfficeEditor from './OnlyOfficeEditor';

// Email choice the review starts from: the template's setting.
const emailFromDraft = (draft) => ({
  send: Boolean(draft?.emailDelivery),
  toEmployee: Boolean(draft?.emailDelivery) && Boolean(draft?.employeeEmail),
  toHr: draft?.emailDelivery === 'employeeAndHr',
  extra: [],
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Review step before a custom employee document is created: the template is
 * already filled with the employee's data, and anything can still be changed
 * by hand. Editor templates open in the HTML editor, Word templates in the
 * Word editor (ONLYOFFICE). Nothing exists until "Create document"; the
 * document can be emailed as a PDF at the same time.
 *
 * @param {Object}   draft      - `{ draftId, source, contentHtml?, editor?, emailDelivery, employeeEmail }`.
 * @param {string}   title      - Document title shown in the header.
 * @param {boolean}  creating   - The create request is running.
 * @param {Function} onCreate   - `(contentHtml | undefined, email) => void`.
 * @param {Function} onCancel   - Closes and discards the draft.
 */
export default function DocumentReviewModal({
  draft,
  title,
  creating = false,
  onCreate,
  onCancel,
}) {
  const { t } = useTranslation(['forms']);
  const [contentHtml, setContentHtml] = useState('');
  const [wordReady, setWordReady] = useState(false);
  const [email, setEmail] = useState(emailFromDraft(null));

  useEffect(() => {
    setContentHtml(draft?.contentHtml || '');
    setWordReady(false);
    setEmail(emailFromDraft(draft));
    // A new draft starts from its template's email setting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.draftId, draft?.contentHtml]);

  const isWord = draft?.source === 'upload';
  const setEmailPart = (patch) => setEmail((current) => ({ ...current, ...patch }));
  const extraValid = email.extra.every((address) => EMAIL_PATTERN.test(address));
  const noRecipient =
    email.send && !email.toEmployee && !email.toHr && email.extra.length === 0;

  return (
    <Modal
      open={Boolean(draft)}
      title={
        <div>
          <div>{t('forms:reviewDocumentTitle', 'Pregled i izmena pre kreiranja')}</div>
          <Typography.Text type="secondary" className="!text-sm !font-normal">
            {title}
          </Typography.Text>
        </div>
      }
      width="min(1200px, 96vw)"
      style={{ top: 16 }}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      onCancel={onCancel}
      footer={
        <div className="flex flex-col gap-3 text-left">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Checkbox
              checked={email.send}
              onChange={(e) => setEmailPart({ send: e.target.checked })}
            >
              {t('forms:sendByEmail', 'Pošalji mejlom')}
            </Checkbox>
            {email.send && (
              <>
                <Checkbox
                  checked={email.toEmployee}
                  disabled={!draft?.employeeEmail}
                  onChange={(e) => setEmailPart({ toEmployee: e.target.checked })}
                >
                  {draft?.employeeEmail
                    ? t('forms:sendToEmployee', {
                        defaultValue: 'Zaposlenom ({{email}})',
                        email: draft.employeeEmail,
                      })
                    : t('forms:employeeHasNoEmail', 'Zaposleni nema mejl u kartonu')}
                </Checkbox>
                <Checkbox
                  checked={email.toHr}
                  onChange={(e) => setEmailPart({ toHr: e.target.checked })}
                >
                  {t('forms:sendToHr', 'HR-u')}
                </Checkbox>
                <Select
                  mode="tags"
                  size="small"
                  open={false}
                  suffixIcon={null}
                  tokenSeparators={[',', ';', ' ']}
                  value={email.extra}
                  onChange={(values) => setEmailPart({ extra: values })}
                  placeholder={t('forms:extraEmails', 'Dodatne adrese')}
                  status={extraValid ? undefined : 'error'}
                  style={{ minWidth: 260, flex: 1 }}
                />
              </>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Typography.Text type="secondary" className="text-xs">
              {noRecipient
                ? t('forms:emailNoRecipient', 'Izaberite bar jednog primaoca ili isključite slanje.')
                : isWord
                  ? t(
                      'forms:reviewWordHint',
                      'Izmene se čuvaju same. Dokument nastaje tek kad kliknete „Kreiraj dokument".'
                    )
                  : t(
                      'forms:reviewHtmlHint',
                      'Potpis direktora dodaje se sam na kraju dokumenta. Dokument nastaje tek kad kliknete „Kreiraj dokument".'
                    )}
            </Typography.Text>
            <div className="flex shrink-0 gap-2">
              <Button
                className="btn-default btn-default--hover"
                onClick={onCancel}
                disabled={creating}
              >
                {t('forms:cancel', 'Otkaži')}
              </Button>
              <Button
                type="primary"
                className="btn-primary"
                loading={creating}
                disabled={(isWord && !wordReady) || noRecipient || !extraValid}
                onClick={() => onCreate?.(isWord ? undefined : contentHtml, email)}
              >
                {t('forms:createDocument', 'Kreiraj dokument')}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      {draft && isWord && (
        <div style={{ height: 'calc(100vh - 270px)', minHeight: 420 }}>
          <OnlyOfficeEditor
            documentServerUrl={draft.editor?.documentServerUrl}
            config={draft.editor?.config}
            onReady={() => setWordReady(true)}
          />
        </div>
      )}
      {draft && !isWord && (
        <div style={{ maxHeight: 'calc(100vh - 270px)', overflowY: 'auto' }}>
          <DocumentTemplateEditor
            value={contentHtml}
            onChange={setContentHtml}
            showPalette={false}
          />
        </div>
      )}
    </Modal>
  );
}
